import React, { useMemo, useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { deleteWorkTypeMaterial } from "../../helpers/estimates";
import {
  computeMaterialLineTotal,
  computeWorkTypeMaterialsTotal,
  formatCurrency,
  getMaterialUnitCost,
} from "../../helpers/estimateTotals";
import { EstimateOverflowMenu } from "./EstimateOverflowMenu";
import { MaterialFormSheet } from "./MaterialFormSheet";

function materialMeta(row, materialsCatalog) {
  const quantity = row.quantity != null && row.quantity !== "" ? Number(row.quantity) : null;
  const unitCost = getMaterialUnitCost(row, materialsCatalog);
  const unitLabel = row.unit_of_measure_snapshot ? ` / ${row.unit_of_measure_snapshot}` : "";
  if (quantity == null) {
    return unitCost > 0 ? `${formatCurrency(unitCost)}${unitLabel}` : "";
  }
  return `${quantity} × ${formatCurrency(unitCost)}${unitLabel}`;
}

/**
 * Compact Materials section — same visual language (row / add / overflow)
 * as Measurements and Labour. Reuses addWorkTypeMaterial (via
 * MaterialFormSheet) / deleteWorkTypeMaterial unchanged; catalog, Other
 * Product, quantity, and unit-cost behavior are untouched. Material
 * quantity remains independent of measured sqft.
 */
export function FeatureMaterialsSection({
  workType,
  materialsCatalog,
  editable,
  onReload,
}) {
  const { actor } = usePermissions();
  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const materials = workType.materials || [];
  const materialsTotal = useMemo(
    () => computeWorkTypeMaterialsTotal(workType, materialsCatalog),
    [workType, materialsCatalog],
  );

  async function handleDelete(row) {
    if (!editable) return;
    if (!window.confirm(`Remove "${row.material_name_snapshot}" from this feature?`)) {
      return;
    }
    try {
      await deleteWorkTypeMaterial(actor, row.room_material_id);
      await onReload?.();
    } catch (err) {
      console.error("[FeatureMaterialsSection] Delete failed:", err);
      alert(err.message || "Unable to remove material.");
    }
  }

  return (
    <div className="est-section-block">
      <div className="est-section-summary-row">
        <span className="est-section-summary-label">Materials</span>
        {materialsTotal > 0 && (
          <span className="est-section-summary-value">
            {formatCurrency(materialsTotal)}
          </span>
        )}
      </div>

      {materials.length === 0 ? (
        <div className="est-home-empty-row">No materials yet.</div>
      ) : (
        materials.map((row) => (
          <div
            key={row.room_material_id}
            className="est-row"
            role={editable ? "button" : undefined}
            tabIndex={editable ? 0 : undefined}
            onClick={() => editable && setEditingRow(row)}
          >
            <div className="est-row-main">
              <div className="est-row-title">{row.material_name_snapshot}</div>
              <div className="est-row-meta">{materialMeta(row, materialsCatalog)}</div>
            </div>
            <div className="est-row-value">
              {formatCurrency(computeMaterialLineTotal(row, materialsCatalog))}
            </div>
            {editable && (
              <EstimateOverflowMenu
                ariaLabel="Material actions"
                items={[
                  { label: "Remove", onClick: () => handleDelete(row), destructive: true },
                ]}
              />
            )}
          </div>
        ))
      )}

      {editable && (
        <button
          type="button"
          className="est-add-row-btn"
          onClick={() => setShowAdd(true)}
        >
          + Add Material
        </button>
      )}

      {showAdd && (
        <MaterialFormSheet
          estimateFeatureId={workType.estimate_feature_id}
          materialsCatalog={materialsCatalog}
          onClose={() => setShowAdd(false)}
          onSaved={onReload}
        />
      )}

      {editingRow && (
        <MaterialFormSheet
          estimateFeatureId={workType.estimate_feature_id}
          materialsCatalog={materialsCatalog}
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={onReload}
        />
      )}
    </div>
  );
}
